import React, {useEffect, useState} from 'react';
import {PageHeader} from "../../../components/PageHeader";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    approveRentalRequest,
    getRequestRentalWithRentalAsset, rejectRentalRequest,
    returnRentalRequest
} from "../../../api/lifecycle/rental/rentalRequestService";
import {
    ContainerTitle,
    InfoContainer,
    InfoContainerTitle,
    InfoItem,
    InfoTitle,
    RentalInfo,
    SubContainer
} from "../rentalAsset/rentalAssetViewCss";
import {calculateLeftDateRate, calculateRemainingDays, handleSetUserInfo} from "../../../components/Utils/SimmsUtils";
import RentalExtendModal from "../rentalAsset/subPage/RentalExtendModal";
import {useDispatch, useSelector} from "react-redux";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import {changeRefreshRentalRequest} from "../../../redux/slice/statusSlice";
import {RentalType} from "../../../constant/CodeSpecNum";
import RentalHistory from "../rentalHistory/RentalHistory";

/** YYYY-MM-DD(THH:mm:ss) -> YY-MM-DD */
const formatShortDate = (value) => {
    if (!value) return "";
    const datePart = String(value).split("T")[0];
    return datePart.length >= 10 ? datePart.slice(2) : datePart;
};

/** 대여신청관리 대여기간 진행바 (얇은 라운드 트랙 + 그라디언트 필) */
const RentalPeriodProgress = ({startDate, endDate}) => {
    const done = Math.min(Math.max(calculateLeftDateRate(startDate, endDate), 0), 100);
    const overdue = calculateRemainingDays(endDate).includes("지남");
    return (
        <div className={'mt-[6px] px-[4px]'}>
            <div className={'relative h-[8px] w-full overflow-hidden rounded-full bg-[#eef1f5]'}>
                <div
                    className={'absolute left-0 top-0 h-full rounded-full transition-[width] duration-500 ease-out'}
                    style={{
                        width: `${done}%`,
                        background: overdue
                            ? 'linear-gradient(90deg, #fb923c, #ef4444)'
                            : 'linear-gradient(90deg, #60a5fa, #2563eb)',
                    }}
                />
            </div>
        </div>
    );
};

const RentalRequestAdminView = () => {
    const [rentalRequest, setRentalRequest] = useState({});

    const refresh = useSelector(state => state.status.refreshRentalRequest);
    const {t} = useTranslation();
    const {id} = useParams();

    useEffect(() => {
        if (!id) return;
        setRentalRequest({});
        handleCallDate();
    }, [id, refresh]);

    const handleCallDate = async () => {
        if (id) {
            await getRequestRentalWithRentalAsset(id, (data) => {
                setRentalRequest({...data});
            });
        }
    }

    return (
        <div className="common-container border-l-gray" style={{height: "calc(100vh - 30rem)", display: "flex", flexDirection: "column"}}>
            <PageHeader header={"대여신청관리"} indicator={"라이프사이클 > 자산신청관리 > 상세"}/>
            <div className={'flex border border-gray-300 p-2 gap-6 justify-between shrink-0'}>
                <SubContainer className={'bg-[#f5f5f5] w-[500px]'}>
                    <ContainerTitle>대여자산 정보</ContainerTitle>
                    <InfoContainer>
                        <RentalInfo>
                            <InfoTitle>자산명</InfoTitle>
                            <InfoItem>{rentalRequest.assetName}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>자산코드</InfoTitle>
                            <InfoItem>{rentalRequest.assetCode}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여그룹</InfoTitle>
                            <InfoItem>{rentalRequest.codeName}</InfoItem>
                        </RentalInfo>
                    </InfoContainer>
                </SubContainer>
                <SubContainer className={'flex-1 h-full'}>
                    <ContainerTitle>대여 정보</ContainerTitle>
                    <RentalAssetInfo rentalRequest={rentalRequest}/>
                </SubContainer>
            </div>
            <div className={'border border-gray-300 px-3 pt-2 pb-1 mt-1'} style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <RentalHistory rentalInfo={{id: rentalRequest.id}} showRentalRequestHistory/>
            </div>
        </div>
    );
};

export default RentalRequestAdminView;

const RentalAssetInfo = ({rentalRequest}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const handleReturn = async () => {
        if (window.confirm(t(`confirmReturnRental`))) {
            const data = {
                rentalAssetId: rentalRequest.rentalAssetId,
                rentalRequestId: rentalRequest.id,
            };
            await returnRentalRequest(data, () => {
                handleActiveToast({message: t(`complete`)});
            }, () => {
                handleActiveError(
                    {
                        message: t(`unableToHandle.message`),
                        description: t(`unableToHandle.statusAlreadyChanged`)
                    });
            });
            dispatch(changeRefreshRentalRequest());
        }
    }

    const handleApprove = async () => {
        if (window.confirm(t(`confirmRent`))) {
            const data = {
                rentalAssetId: rentalRequest.rentalAssetId,
                rentalRequestId: rentalRequest.id,
            };
            await approveRentalRequest(data, () => {
                handleActiveToast({message: t(`complete`)});
            }, () => {
                handleActiveError(
                    {
                        message: t(`unableToHandle.message`),
                        description: t(`unableToHandle.statusAlreadyChanged`)
                    });
            });
            dispatch(changeRefreshRentalRequest());
        }
    }

    const handleReject = async () => {
        if (window.confirm(t(`confirmRejectRental`))) {
            const data = {
                rentalAssetId: rentalRequest.rentalAssetId,
                rentalRequestId: rentalRequest.id,
                rentalType: RentalType.REJECT.value,
            }
            await rejectRentalRequest(data, () => {
                handleActiveToast({message: t(`complete`)});
            }, () => {
                handleActiveError(
                    {
                        message: t(`unableToHandle.message`),
                        description: t(`unableToHandle.statusAlreadyChanged`)
                    });
            });
            dispatch(changeRefreshRentalRequest());
        }
    }

    return (
        <>
            <div className={"flex gap-[2rem] mx-8 justify-center"}>
                <InfoContainer className={"w-[22rem]"}>
                    <InfoContainerTitle>신청 정보</InfoContainerTitle>
                    <div className={"mt-3"}>
                        <RentalInfo>
                            <InfoTitle>대여 신청일</InfoTitle>
                            <InfoItem className={'w-[15rem]'}>{rentalRequest?.createdAt}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 신청자</InfoTitle>
                            <InfoItem className={'w-[15rem]'}>{handleSetUserInfo(rentalRequest.rentUser)}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 상태</InfoTitle>
                            <InfoItem className={'w-[15rem]'}>{rentalRequest.rentalTypeName}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 기간</InfoTitle>
                            {(() => {
                                const periodEnd = rentalRequest.extendDate || rentalRequest.endDate;
                                const isRent = rentalRequest.rentalType === RentalType.RENT.value;
                                const overdue = calculateRemainingDays(periodEnd).includes("지남");
                                return (
                                    <div className={'w-[15rem]'}>
                                        <div className={"flex items-center justify-between gap-2 px-[4px]"}>
                                            <span className={'text-[13px] text-[#333]'}>
                                                {formatShortDate(rentalRequest.startDate)} ~ {formatShortDate(periodEnd)}
                                            </span>
                                            {isRent &&
                                                <span className={`text-[12px] font-semibold ${overdue ? 'text-red-500' : 'text-[#2563eb]'}`}>
                                                    {calculateRemainingDays(periodEnd)}
                                                </span>
                                            }
                                        </div>
                                        {isRent &&
                                            <RentalPeriodProgress startDate={rentalRequest.startDate} endDate={periodEnd}/>
                                        }
                                    </div>
                                );
                            })()}
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 연장일</InfoTitle>
                            <InfoItem className={'w-[15rem]'}>{rentalRequest.extendDate ? `${rentalRequest.extendDate} (기존 반납일: ${rentalRequest.endDate}}` : ""}</InfoItem>
                        </RentalInfo>
                    </div>
                </InfoContainer>
                <InfoContainer className={"w-[20rem]"}>
                    <InfoContainerTitle>대여 담당자 정보</InfoContainerTitle>
                    <div className={"mt-3"}>
                        <RentalInfo>
                            <InfoTitle>대여 실행일</InfoTitle>
                            <InfoItem>{rentalRequest?.rentApproveDate}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 승인자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.rentApprover)}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>기간연장 실행일</InfoTitle>
                            <InfoItem>{rentalRequest?.extendApproveDate}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>기간연장 승인자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.extendApprover)}</InfoItem>
                        </RentalInfo>
                    </div>
                </InfoContainer>
                <InfoContainer className={"w-[20rem]"}>
                    <InfoContainerTitle>반납 담당자 정보</InfoContainerTitle>
                    <div className={"mt-3"}>
                        <RentalInfo>
                            <InfoTitle>반납 승인일</InfoTitle>
                            <InfoItem>{rentalRequest?.returnDate}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>반납 승인자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.returnApprover)}</InfoItem>
                        </RentalInfo>
                    </div>
                </InfoContainer>
            </div>
            <div className={"flex item-center mt-5 gap-2"}>
                {/* 대여 신청 */}
                {rentalRequest.rentalType === RentalType.REQUEST.value &&
                    <>
                        <button
                            className={'text-[13px] px-4 py-1 rounded-4 border text-white bg-themeDark hover:opacity-90'}
                            onClick={handleApprove}>대여승인</button>
                        <button
                            className={'text-[13px] px-4 py-1 rounded-4 border border-red-300 text-red-500 bg-white hover:bg-red-50'}
                            onClick={handleReject}>대여거절</button>
                    </>
                }
                {/* 대여 반납 */}
                {rentalRequest.rentalType === RentalType.RETURN.value && <div className={'h-[30px]'}>반납완료되었습니다.</div>}
                {/* 대여중 */}
                {rentalRequest.rentalType === RentalType.RENT.value &&
                    <>
                        <button
                            className={'text-[13px] px-4 py-1 rounded-4 border border-green-400 text-green-600 bg-white hover:bg-green-50'}
                            onClick={handleReturn}>대여반납</button>
                        {!rentalRequest.extendDate && <RentalExtendModal rentalRequest={rentalRequest}/>}
                    </>
                }
            </div>
        </>
    );
}
